<script>
import MixedGridFilters from '@phoenix/mixins/components/mixedGrid/MixedGridFilters';
import Panel from '@maison/components/common/Panel.vue';
import { Teleport } from 'vue';
import { showPanel } from '@phoenix/helpers/panel-helper';
import { keys } from 'lodash-es';
import FormGroup from '@maison/components/form/FormGroup.vue';
import CheckboxField from '@maison/components/form/fields/CheckboxField.vue';
import MixedGridSortSelect from '@maison/components/mixedGrid/MixedGridSortSelect.vue';
import MixedGridFiltersItemList from '@maison/components/mixedGrid/MixedGridFiltersItemList.vue';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import {
  PhxsgAccordion,
  PhxsgAccordionContent,
  PhxsgAccordionItem,
  PhxsgAccordionTrigger,
} from '@sg/src/View/Components/Molecules/Accordion';

export default {
  components: {
    MixedGridFiltersItemList,
    MixedGridSortSelect,
    CheckboxField,
    FormGroup,
    Panel,
    Teleport,
    PhxsgButton,
    PhxsgAccordion,
    PhxsgAccordionContent,
    PhxsgAccordionItem,
    PhxsgAccordionTrigger,
  },

  mixins: [MixedGridFilters],

  props: {
    highlightedFilterId: {
      type: String,
      default: '',
    },
  },

  emits: ['onSortChange', 'onFiltersChanged', 'filtersInitialized'],

  computed: {
    panelId() {
      return `${this.uniqueId}-filters-panel`;
    },

    accordionId() {
      return `${this.uniqueId}-accordion`;
    },

    filtersButtonLabel() {
      const label = this.$t('MixedGrid.filters.button');

      return this.hasFilters
        ? this.$t('MixedGrid.filters.buttonWithActiveFilters', { label, count: keys(this.activeFilters).length })
        : label;
    },

    productCountLabel() {
      return this.productsCount > 0
        ? this.$t('MixedGrid.filters.productsCount', { count: this.productsCount })
        : this.$t('MixedGrid.filters.noProducts');
    },

    displayedFilters() {
      return this.filters.filter((item) => item.id !== this.highlightedFilterId);
    },

    highlightedFilter() {
      return this.getFilterById(this.highlightedFilterId);
    },
  },

  methods: {
    openPanel() {
      showPanel(this.panelId);
    },

    resetAll() {
      this.removeAllFilters();
      this.currentSort = this.defaultSort;
    },

    getAccordionItemId(index) {
      return `${this.uniqueId}-accordion-${index}`;
    },
  },
};
</script>

<template>
  <div class="cluster cluster--spacing-4 justify-end">
    <div v-if="shouldDisplayProductsCount" class="flex items-center gap-1">{{ productCountLabel }}</div>

    <PhxsgButton data-cy="mixed-grid-filters-panel-open-btn" @click="openPanel">
      {{ filtersButtonLabel }}
    </PhxsgButton>
  </div>

  <teleport to="#teleport-target-panels">
    <panel
      :id="panelId"
      :classes="['dialog']"
      :title="$t('MixedGrid.filters.panelTitle')"
      open-from="end"
      boxed-content
      data-cy="mixed-grid-filters"
    >
      <template #content>
        <div class="stack stack--spacing-4">
          <div class="stack stack--spacing-4">
            <mixed-grid-sort-select v-model="currentSort" :unique-id="uniqueId" />

            <form-group
              v-show="shouldDisplaySellable"
              :attrs="{
                id: `${uniqueId}-available-online`,
                name: 'available-online',
                label: $t('MixedGrid.filters.availableOnline'),
                type: 'checkbox',
              }"
              is-fieldset
              :show-label="false"
            >
              <checkbox-field v-model="availableOnline" :should-validate="false" />
            </form-group>
          </div>
          <div>
            <PhxsgAccordion :id="accordionId" class="stack stack--spacing-2" role="presentation" type="multiple">
              <PhxsgAccordionItem
                v-for="(filter, index) in displayedFilters"
                :id="getAccordionItemId(index)"
                :key="`filter-${filter.id}`"
                role="presentation"
              >
                <PhxsgAccordionTrigger>
                  {{ filter.label }}
                </PhxsgAccordionTrigger>

                <PhxsgAccordionContent>
                  <mixed-grid-filters-item-list
                    v-if="filter.component === 'list'"
                    :filter="filter"
                    :active-filters="activeFilters"
                    :empty-filters="emptyFilters"
                    :unique-id="uniqueId"
                    @on-item-selected="toggleFilter"
                  />
                </PhxsgAccordionContent>
              </PhxsgAccordionItem>
            </PhxsgAccordion>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="switcher switcher--spacing-4 filters-panel__actions">
          <PhxsgButton
            variant="plain"
            color="primary"
            class="filters-panel__actions-view"
            :data-panel-close="panelId"
            data-cy="mixed-grid-filters-panel-submit-btn"
          >
            {{ $t('MixedGrid.filters.displayResults', { count: productsCount }) }}
          </PhxsgButton>

          <PhxsgButton variant="fade" @click="resetAll">
            {{ $t('MixedGrid.filters.clearFilters') }}
          </PhxsgButton>
        </div>
      </template>
    </panel>
  </teleport>
</template>
